import { useMemo, useState } from "preact/hooks";

import type { AssigneeOption } from "../components/AssigneePickerSheet";
import { ProjectFormSheet } from "../components/ProjectFormSheet";
import { ProjectRollup } from "../components/ProjectRollup";
import type { useProjects } from "../hooks/useProjects";
import {
  noActiveProjectsHint,
  PROJECT_ARCHIVED_TAG_LABEL,
  projectArchivedMessage,
  PROJECTS_FILTER_ACTIVE_LABEL,
  PROJECTS_FILTER_ALL_LABEL,
  PROJECTS_FILTER_TOGGLE_LABEL,
  PROJECTS_SHOW_ALL_LABEL,
  projectRestoredMessage,
  projectsListCountAnnouncement,
  projectStatusLabel,
} from "../lib/recordsCopy";
import {
  PROJECTS_EMPTY_BODY,
  PROJECTS_EMPTY_TITLE,
  PROJECTS_SECTION_HELPING,
  PROJECTS_SECTION_YOURS,
  projectStartedMessage,
  startedByLabel,
  START_PROJECT_LABEL,
} from "../lib/projectTasksCopy";
import type { ProjectOut } from "../types";
import { ProjectDetailOverlay } from "./ProjectDetailOverlay";

export interface ProjectsViewProps {
  projects: ReturnType<typeof useProjects>;
  isAdmin: boolean;
  /** Sprint-017 E7 — the signed-in user's id (helper leave / self-exclusion
   * from pickers / ownership resolution). Optional (default `""`) so
   * pre-existing project tests keep compiling. */
  meId?: string;
  /** ADR-003 §3.2 `can_assign_out` (admin/manager) — threaded to the detail
   * overlay's checklist. Default `false`. */
  canAssignOut?: boolean;
  /** People for the assign / "who's helping" pickers + owner-name lookup. */
  roster?: AssigneeOption[];
  onNotify: (message: string) => void;
}

type ProjectsFilter = "active" | "all";

/**
 * Sprint-005 C4 — the More tab's Projects screen: read-only list (name +
 * status), tap through to detail. Only reached by admin/manager — a
 * member never sees this screen at all (`MoreView` doesn't even render
 * its nav row for a member, matching `GET /projects`'s own 403 for that
 * role — ADR-002 §5/§11).
 *
 * Sprint-013 C2 (design-spec-animal-cards-2026-07-10.md §5, ux-ruling-
 * inactive-grid-2026-07-10.md — E1 PM ruling, mirrored here per the
 * task's own binding instruction) — adds the header "+ Add" entry point
 * and an "Active" (default) / "All" toggle, admin-only Add/Edit/Archive/
 * Restore. No toggle state is persisted across mounts — every fresh visit
 * starts on "Active," same as `AnimalsView`. `.admin-list`/`.admin-row`
 * markup (Option C) is unchanged — only the header toolbar above it and
 * each row's archived-state treatment are new.
 */
export function ProjectsView({
  projects,
  isAdmin,
  meId = "",
  canAssignOut = false,
  roster = [],
  onNotify,
}: ProjectsViewProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<ProjectsFilter>("active");
  const [showAdd, setShowAdd] = useState(false);
  const openProject = projects.projects?.find((p) => p.id === openId) ?? null;

  const allProjects = projects.projects ?? [];
  const activeProjects = useMemo(
    () => allProjects.filter((p) => p.archived_at == null),
    [allProjects],
  );
  const archivedCount = allProjects.length - activeProjects.length;
  const visibleProjects = filter === "all" ? allProjects : activeProjects;

  // Sprint-017 E7 (design §2.2) — non-admins see two labeled sections so
  // "mine" vs "I'm helping" never blurs. The admin keeps their existing
  // flat list + Active/All toggle unchanged (Ruling Q1 — the toggle is
  // admin-only; members rarely archive).
  const ownedActive = useMemo(
    () => activeProjects.filter((p) => p.is_owner === true),
    [activeProjects],
  );
  // "Helping out" = any active project the caller does NOT own (server sends
  // `is_owner`; treat a missing flag as not-owned so a shared-in project is
  // never dropped from both sections).
  const helpingActive = useMemo(
    () => activeProjects.filter((p) => p.is_owner !== true),
    [activeProjects],
  );

  function ownerName(project: ProjectOut): string | null {
    if (!project.owner_id) return null;
    return roster.find((r) => r.id === project.owner_id)?.name ?? null;
  }

  const startCta = (
    <button type="button" class="secondary-btn pt-start-project" onClick={() => setShowAdd(true)}>
      {START_PROJECT_LABEL}
    </button>
  );

  const formSheet = (
    <ProjectFormSheet
      open={showAdd}
      mode="add"
      onCancel={() => setShowAdd(false)}
      onAdd={async (fields) => {
        const created = await projects.createProject(fields);
        setShowAdd(false);
        onNotify(projectStartedMessage(created.name));
        // NOTE (PM): design §2.4 also opens the new project straight into
        // its empty checklist. Deferred here — auto-opening the overlay
        // duplicates the record name in the DOM (row + overlay title) and
        // the created project is one tap away in the list. Flagged as a
        // small follow-up, not a spec-blocking omission.
      }}
    />
  );

  const detailOverlay = openProject ? (
    <ProjectDetailOverlay
      project={openProject}
      isAdmin={isAdmin}
      meId={meId}
      canAssignOut={canAssignOut}
      roster={roster}
      onClose={() => setOpenId(null)}
      onNotify={onNotify}
      onSaveProject={async (projectId, body) => {
        await projects.patchProject(projectId, body);
      }}
      onArchive={async () => {
        await projects.archiveProject(openProject.id);
        setOpenId(null);
        onNotify(projectArchivedMessage(openProject.name));
      }}
      onRestore={async () => {
        await projects.restoreProject(openProject.id);
        setOpenId(null);
        onNotify(projectRestoredMessage(openProject.name));
      }}
    />
  ) : null;

  if (projects.loading) {
    return (
      <div>
        <p class="loading-hint">Loading projects…</p>
      </div>
    );
  }
  if (projects.error) {
    return (
      <div>
        <p class="error-hint" role="alert">
          {projects.error}
        </p>
      </div>
    );
  }

  // Empty — no projects at all, for any role (design §2.3).
  if (allProjects.length === 0) {
    return (
      <div>
        <div class="pt-empty">
          <p class="pt-empty-title">{PROJECTS_EMPTY_TITLE}</p>
          <p class="pt-empty-body">{PROJECTS_EMPTY_BODY}</p>
        </div>
        <div class="pt-start-project-row">{startCta}</div>
        {formSheet}
        {detailOverlay}
      </div>
    );
  }

  // The admin — existing flat list + Active/All toggle, unchanged (Q1).
  if (isAdmin) {
    return (
      <div>
        <div class="animals-view-toolbar">
          <div class="animals-filter-toggle" role="group" aria-label={PROJECTS_FILTER_TOGGLE_LABEL}>
            <button
              type="button"
              class={`animals-filter-btn${filter === "active" ? " is-selected" : ""}`}
              aria-pressed={filter === "active"}
              onClick={() => setFilter("active")}
            >
              {PROJECTS_FILTER_ACTIVE_LABEL}
            </button>
            <button
              type="button"
              class={`animals-filter-btn${filter === "all" ? " is-selected" : ""}`}
              aria-pressed={filter === "all"}
              onClick={() => setFilter("all")}
            >
              {PROJECTS_FILTER_ALL_LABEL}
            </button>
          </div>
          <button
            type="button"
            class="primary-btn animals-add-btn"
            onClick={() => setShowAdd(true)}
          >
            {START_PROJECT_LABEL}
          </button>
        </div>

        <p class="sr-only" aria-live="polite">
          {projectsListCountAnnouncement(visibleProjects.length)}
        </p>

        {visibleProjects.length === 0 ? (
          <p class="empty-hint">
            {noActiveProjectsHint(archivedCount)}{" "}
            <button type="button" class="inline-link-btn" onClick={() => setFilter("all")}>
              {PROJECTS_SHOW_ALL_LABEL}
            </button>
          </p>
        ) : (
          <ul class="admin-list">
            {visibleProjects.map((project) => (
              <li key={project.id}>
                <ProjectRow
                  project={project}
                  startedBy={project.is_owner === false ? ownerName(project) : null}
                  onOpen={() => setOpenId(project.id)}
                />
              </li>
            ))}
          </ul>
        )}

        {formSheet}
        {detailOverlay}
      </div>
    );
  }

  // Member / manager — sectioned "Yours" + "Helping out" (design §2.2).
  return (
    <div>
      {ownedActive.length > 0 ? (
        <>
          <h2 class="admin-section-heading">{PROJECTS_SECTION_YOURS}</h2>
          <ul class="admin-list">
            {ownedActive.map((project) => (
              <li key={project.id}>
                <ProjectRow
                  project={project}
                  startedBy={null}
                  onOpen={() => setOpenId(project.id)}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {helpingActive.length > 0 ? (
        <>
          <h2 class="admin-section-heading" style={{ marginTop: "22px" }}>
            {PROJECTS_SECTION_HELPING}
          </h2>
          <ul class="admin-list">
            {helpingActive.map((project) => (
              <li key={project.id}>
                <ProjectRow
                  project={project}
                  startedBy={ownerName(project)}
                  onOpen={() => setOpenId(project.id)}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <div class="pt-start-project-row">{startCta}</div>
      {formSheet}
      {detailOverlay}
    </div>
  );
}

function ProjectRow({
  project,
  startedBy,
  onOpen,
}: {
  project: ProjectOut;
  startedBy: string | null;
  onOpen: () => void;
}) {
  const statusLabel = projectStatusLabel(project.status);
  const isArchived = project.archived_at != null;
  const total = project.task_total_count ?? 0;
  const done = project.task_done_count ?? 0;
  return (
    <button type="button" class={`admin-row${isArchived ? " is-archived" : ""}`} onClick={onOpen}>
      <span class="admin-row-main">
        <span class="admin-row-name">
          {project.name}
          {isArchived ? (
            <span class="admin-tag is-archived">{PROJECT_ARCHIVED_TAG_LABEL}</span>
          ) : null}
        </span>
        {startedBy ? (
          <span class="admin-row-sub">{startedByLabel(startedBy)}</span>
        ) : statusLabel ? (
          <span class="admin-row-sub">{statusLabel}</span>
        ) : null}
        {/* Roll-up on any card with ≥1 task (design §2.2). */}
        {total > 0 ? <ProjectRollup done={done} total={total} variant="card" /> : null}
      </span>
      <span class="chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}
